import { create } from 'zustand';
import { useAdminAccess } from '@/shared/model/adminAccess';
import { authService } from './api/authService';
import type { AuthState, AuthTokens } from './model/types';
import { clearApiCache, refreshAllApiInstances } from '@/core/api/generatedApiClient';
import { SessionChangedError, SessionExpiredError } from '@/core/api/sessionRefresh';
import {
    clearTokens,
    getAccessToken,
    getRefreshToken,
    getRoleCode,
    setTokens,
    subscribeRoleCode,
} from '@/core/utils/tokenUtils';

/** Открывает полученную сессию сотрудника */
const applyTokens = (tokens: AuthTokens) => {
    useAdminAccess.getState().clear();

    setTokens(
        tokens.accessToken,
        tokens.refreshToken,
        tokens.userId,
        tokens.login,
        tokens.role,
        tokens.roleCode,
    );

    refreshAllApiInstances();
};

/** Хранит разделяемое состояние сессии сотрудника */
export const useAuth = create<AuthState>((set, get) => {
    const hasToken = Boolean(getAccessToken());
    const persistedRoleCode = getRoleCode();

    Promise.resolve()
        .then(() => {
            const refreshToken = getRefreshToken();
            const needsRoleHydration = hasToken && !persistedRoleCode && Boolean(refreshToken);

            if (hasToken && !needsRoleHydration) {
                set({ isCheckingAuth: false });
            } else if (refreshToken) {
                void get()
                    .refresh()
                    .catch(() => undefined);
            } else {
                set({
                    isCheckingAuth: false,
                    isAuthenticated: false,
                    roleCode: null
                });
            }
        });

    return {
        isAuthenticated: hasToken,
        isCheckingAuth: true,
        roleCode: persistedRoleCode,
        sessionRevision: 0,

        completeLogin: (tokens) => {
            applyTokens(tokens);

            set({
                isAuthenticated: true,
                isCheckingAuth: false,
                roleCode: tokens.roleCode ?? null,
                sessionRevision: get().sessionRevision + 1,
            });
        },

        logout: () => {
            useAdminAccess.getState().clear();
            clearTokens();
            clearApiCache();

            set({
                isAuthenticated: false,
                isCheckingAuth: false,
                roleCode: null
            });
        },

        refresh: async () => {
            set({ isCheckingAuth: true });

            try {
                const tokens = await authService.refresh();

                const isCurrentSession = tokens.accessToken === getAccessToken()
                    && tokens.refreshToken === getRefreshToken();

                if (!isCurrentSession) throw new SessionChangedError();

                useAdminAccess.getState().clear();
                refreshAllApiInstances();

                set({
                    isAuthenticated: true,
                    isCheckingAuth: false,
                    roleCode: tokens.roleCode ?? null,
                    sessionRevision: get().sessionRevision + 1,
                });
            } catch (error: unknown) {
                if (error instanceof SessionExpiredError) clearApiCache();

                set({
                    isAuthenticated: Boolean(getAccessToken() || getRefreshToken()),
                    isCheckingAuth: false,
                    roleCode: getRoleCode(),
                });

                throw error;
            }
        },
    };
});

subscribeRoleCode((roleCode) => {
    if (useAuth.getState().roleCode !== roleCode) useAdminAccess.getState().clear();

    useAuth.setState({
        roleCode,
        isAuthenticated: Boolean(getAccessToken()),
    });
});
